import type { TFunction } from 'i18next';
import { Dimension, NoteCode, ReasonCode } from '@optiflow/frame-recommendation';
import {
  FaceShape,
  FrameShape,
  type FrameRecommendationItem,
  type RecommendationExplanation,
} from '@optiflow/types';

/**
 * The sentences behind reason and note codes.
 *
 * The rules send codes and parameters, never prose; every word a card shows is written here and in the locale files. The maps are typed by the package's own code catalogues, so a code without a sentence does not compile, and a code this screen has never heard of — from a newer rule version — is left out rather than shown raw.
 */

export interface ExplanationText {
  text: string;
  /** What the sentence refers to — a shape, a size, a colour — shown quieter beside it. */
  detail: string | null;
}

type Params = RecommendationExplanation['params'];

const FACE_SHAPES = new Set<string>(Object.values(FaceShape));
const FRAME_SHAPES = new Set<string>(Object.values(FrameShape));
const DIMENSIONS = new Set<string>(Object.values(Dimension));

const textParam = (params: Params, key: string): string | null => {
  const value = params[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
};

const wholeParam = (params: Params, key: string): number | null => {
  const value = params[key];
  return typeof value === 'number' && Number.isInteger(value) ? value : null;
};

const faceShapeName = (t: TFunction, value: string | null) =>
  value !== null && FACE_SHAPES.has(value) ? t(`face.shapes.${value}`) : null;

const frameShapeName = (t: TFunction, value: string | null) =>
  value !== null && FRAME_SHAPES.has(value) ? t(`catalog.frameShape.${value}`) : null;

const dimensionName = (t: TFunction, value: unknown) =>
  typeof value === 'string' && DIMENSIONS.has(value)
    ? t(`frameRecommendations.dimension.${value}`)
    : null;

/** "Lens 52 mm · Bridge 18 mm" for the sizes that matched. */
function matchedSizes(t: TFunction, params: Params): string | null {
  const entries = Array.isArray(params.dimensions) ? (params.dimensions as unknown[]) : [];
  const parts = entries.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const comparison = entry as Params;
    const name =
      typeof comparison.dimension === 'string' && DIMENSIONS.has(comparison.dimension)
        ? t(`frameRecommendations.dimensionShort.${comparison.dimension}`)
        : null;
    const value = wholeParam(comparison, 'valueMm');
    return name !== null && value !== null
      ? [t('frameRecommendations.dimensionValue', { dimension: name, value })]
      : [];
  });
  return parts.length > 0 ? parts.join(' · ') : null;
}

const REASONS: Record<
  ReasonCode,
  (t: TFunction, params: Params, item: FrameRecommendationItem) => string | null
> = {
  [ReasonCode.FACE_SHAPE_COMPLEMENT]: (t, params) =>
    faceShapeName(t, textParam(params, 'faceShape')),
  [ReasonCode.FACE_SHAPE_COMPLEMENT_BOTH]: (t, params) => {
    const a = faceShapeName(t, textParam(params, 'componentA'));
    const b = faceShapeName(t, textParam(params, 'componentB'));
    return a !== null && b !== null ? t('frameRecommendations.pair', { a, b }) : null;
  },
  [ReasonCode.FACE_SHAPE_COMPLEMENT_ONE]: (t, params) =>
    faceShapeName(t, textParam(params, 'component')),
  [ReasonCode.DIMENSION_MATCH]: (t, params) => matchedSizes(t, params),
  [ReasonCode.MATCHES_PREFERRED_STYLE]: (t, params) =>
    frameShapeName(t, textParam(params, 'frameShape')),
  // The brand's name comes from the frame itself, and only when it is the brand the reason names.
  [ReasonCode.PREFERRED_BRAND]: (_t, params, item) =>
    item.brand !== null && item.brand.id === textParam(params, 'brandId') ? item.brand.name : null,
  [ReasonCode.PREFERRED_COLOUR]: (_t, params) => textParam(params, 'colourName'),
  [ReasonCode.WITHIN_BUDGET]: () => null,
};

const REASON_CODES = new Set<string>(Object.values(ReasonCode));
const NOTE_CODES = new Set<string>(Object.values(NoteCode));

const isReasonCode = (code: string): code is ReasonCode => REASON_CODES.has(code);
const isNoteCode = (code: string): code is NoteCode => NOTE_CODES.has(code);

export function reasonText(
  t: TFunction,
  reason: RecommendationExplanation,
  item: FrameRecommendationItem,
): ExplanationText | null {
  if (!isReasonCode(reason.code)) return null;
  return {
    text: t(`frameRecommendations.reasons.${reason.code}`),
    detail: REASONS[reason.code](t, reason.params, item),
  };
}

const NOTES: Record<NoteCode, (t: TFunction, params: Params) => string> = {
  [NoteCode.OTHER_STYLES_MAY_WORK]: (t) => t('frameRecommendations.notes.OTHER_STYLES_MAY_WORK'),
  [NoteCode.FRAME_SHAPE_NOT_RECORDED]: (t) =>
    t('frameRecommendations.notes.FRAME_SHAPE_NOT_RECORDED'),
  [NoteCode.DIMENSION_OUTSIDE_RANGE]: (t, params) => {
    const dimension = dimensionName(t, params.dimension);
    const value = wholeParam(params, 'valueMm');
    const min = wholeParam(params, 'minMm');
    const max = wholeParam(params, 'maxMm');
    return dimension !== null && value !== null && min !== null && max !== null
      ? t('frameRecommendations.notes.DIMENSION_OUTSIDE_RANGE', { dimension, value, min, max })
      : t('frameRecommendations.notes.DIMENSION_OUTSIDE_RANGE_unnamed');
  },
  [NoteCode.DIMENSION_NOT_RECORDED]: (t, params) => {
    const dimension = dimensionName(t, params.dimension);
    return dimension !== null
      ? t('frameRecommendations.notes.DIMENSION_NOT_RECORDED', { dimension })
      : t('frameRecommendations.notes.DIMENSION_NOT_RECORDED_unnamed');
  },
  [NoteCode.BRAND_NOT_RECORDED]: (t) => t('frameRecommendations.notes.BRAND_NOT_RECORDED'),
  [NoteCode.COLOUR_NOT_RECORDED]: (t) => t('frameRecommendations.notes.COLOUR_NOT_RECORDED'),
};

export function noteText(t: TFunction, note: RecommendationExplanation): string | null {
  return isNoteCode(note.code) ? NOTES[note.code](t, note.params) : null;
}
